// Opens Apple's subscription management from inside the app (iOS ruling B1 §4): a subscription
// bought in the App Store can only be cancelled there, never by our server.
//
// Order: the iOS shell's QlearnSubscriptions plugin (StoreKit's showManageSubscriptions sheet),
// then RevenueCat's managementURL, then Apple's subscriptions page. Loading the plugin and the
// RevenueCat lookup are bounded; the sheet itself is not, because its promise settles only when
// the person closes it.

export const NATIVE_SUBSCRIPTIONS_PLUGIN = 'QlearnSubscriptions';
export const APPLE_SUBSCRIPTIONS_URL = 'https://apps.apple.com/account/subscriptions';
const STEP_BOUND_MS = 4000;

let subscriptionsPlugin = null;

const bounded = (promise) => Promise.race([
  promise,
  new Promise((_, reject) => setTimeout(() => reject(new Error('timed out')), STEP_BOUND_MS)),
]);

// Resolves { plugin } or null. WRAPPED on purpose: a Capacitor plugin proxy answers every
// property as a native method, `then` included, so awaiting or returning the bare proxy from an
// async function calls plugin.then() on the bridge and never settles.
const loadPlugin = async (importCore) => {
  const { Capacitor, registerPlugin } = await importCore();
  if (!Capacitor || typeof Capacitor.isPluginAvailable !== 'function'
      || !Capacitor.isPluginAvailable(NATIVE_SUBSCRIPTIONS_PLUGIN)) {
    return null;
  }
  if (!subscriptionsPlugin) subscriptionsPlugin = registerPlugin(NATIVE_SUBSCRIPTIONS_PLUGIN);
  return { plugin: subscriptionsPlugin };
};

const managementUrlFromRevenueCat = async (importRevenueCat) => {
  const { Purchases } = await importRevenueCat();
  const { isConfigured } = (await Purchases.isConfigured?.()) || {};
  if (!isConfigured) return null;
  const { customerInfo } = (await Purchases.getCustomerInfo()) || {};
  const managementUrl = customerInfo?.managementURL;
  return typeof managementUrl === 'string' && managementUrl.startsWith('https://') ? managementUrl : null;
};

/** @returns {Promise<'sheet'|'url'>} how the management surface was shown */
export const showManageSubscriptions = async ({
  importCore = () => import('@capacitor/core'),
  importRevenueCat = () => import('@revenuecat/purchases-capacitor'),
  importBrowser = () => import('@capacitor/browser'),
} = {}) => {
  try {
    const loaded = await bounded(loadPlugin(importCore));
    if (loaded) {
      await loaded.plugin.showManageSubscriptions();
      return 'sheet';
    }
  } catch (_) {
    // Fall through to a URL: an older app build without the plugin, or no window scene.
  }
  let target = APPLE_SUBSCRIPTIONS_URL;
  try {
    target = (await bounded(managementUrlFromRevenueCat(importRevenueCat))) || APPLE_SUBSCRIPTIONS_URL;
  } catch (_) {
    // Apple's own page works for every App Store subscription.
  }
  try {
    const { Browser } = await importBrowser();
    await Browser.open({ url: target });
  } catch (_) {
    window.open(target, '_blank', 'noopener,noreferrer');
  }
  return 'url';
};

/**
 * After deletion, stop the RevenueCat SDK from acting for the deleted account: its next refresh
 * would otherwise recreate the subscriber the server just deleted. Best effort and bounded.
 */
export const forgetRevenueCatUser = async ({
  importRevenueCat = () => import('@revenuecat/purchases-capacitor'),
} = {}) => {
  try {
    await bounded((async () => {
      const { Purchases } = await importRevenueCat();
      const { isConfigured } = (await Purchases.isConfigured?.()) || {};
      if (isConfigured) await Purchases.logOut();
    })());
  } catch (_) {
    // Already anonymous, or never configured in this session.
  }
};
